/**
 * The per-user and per-organisation reset sets, in one place so every
 * sign-out / organisation-switch transition runs them.
 *
 * ## Why this module exists (SE-8076)
 *
 * This set used to live inside `useSettingsActions.handleResetVar`, which is
 * only reachable through `finalizeSignOut` — and `finalizeSignOut` exists only
 * inside that hook. So the reset was structurally unreachable from anywhere
 * else, and one transition never ran it:
 *
 * | transition | reached the reset? |
 * | -- | -- |
 * | deliberate sign-out | yes, via `finalizeSignOut` |
 * | organisation switch | its own copy of the list |
 * | **token expiry** | **no — nothing ran** |
 *
 * Token expiry signs out from `app/(root)/_layout.tsx` by calling
 * `auth.onCompleteSignOut()` directly, and `handleCompleteSignOut` does only
 * `setValidationCodes([])` and `initializeAuthData(null)`. Every per-user
 * reactive var survived it, and the next user in the same app instance
 * inherited them.
 *
 * That mattered most for the evaluation outbox, whose leak is a mis-attributed
 * WRITE rather than stale UI: `UpsertPlayerEvaluationInput` carries no identity
 * field, so a queued write replays under whoever is authenticated when the
 * drain re-arms (SE-7720). SE-8075 has since taken the outbox OUT of this set —
 * see the note where it used to be cleared.
 *
 * **Expiry is plausibly the more common transition** — sessions expire on their
 * own, while a deliberate sign-out requires someone to choose it.
 *
 * ## Why a plain function rather than a hook
 *
 * The expiry path runs in an effect with **no user and no UI present**, so it
 * cannot call a hook and has nothing to render. Every member of this set is a
 * module-scoped reactive var or a plain function, so none of it needs React —
 * that is what makes one shared callable possible at all.
 *
 * ## What this deliberately does NOT do
 *
 * It does not touch Apollo (`client.clearStore()`), auth storage, or analytics
 * identity. Those are owned by the transition that calls this — a deliberate
 * sign-out also resets the PostHog identity and clears the store; expiry has
 * its own sequencing. This function is exactly the module-scoped per-user
 * state that no store reset can reach, and nothing else.
 *
 * ## `resetPerUserState` vs `resetOrgScopedState` (SE-7721)
 *
 * SE-7721 found a THIRD hand-maintained copy of this list — in
 * `ReceivedInvitationItemContainer`, whose `logIntoOrganization` call is the
 * exact same organisation-switch transition `OrganizationItemContainer` runs.
 * That copy had drifted: it was missing `clearEvaluationOutbox()` and
 * `resetDismissedSuggestions()` (both added to the shared set after this copy
 * was written), AND it never carried `selectedShowTeamVar`, `recentPlayersVar`,
 * `clearDismissedActivityIds()`, or the AsyncStorage wipe that
 * `OrganizationItemContainer` performs.
 *
 * Those four are deliberately **not** part of `resetPerUserState`: they are
 * scoped to the ORGANISATION being left, not the user, and a sign-out does not
 * need them reset because the whole app is being torn down and the reactive
 * vars go with it. An organisation switch keeps the app (and every
 * module-scoped var) alive, so it is the only transition that needs a
 * dedicated clear for org-scoped selections. `resetOrgScopedState` is that
 * clear, and it now has exactly two callers —
 * `OrganizationItemContainer` and `ReceivedInvitationItemContainer` — so
 * there is nowhere left for a third hand-maintained copy to grow.
 *
 * The ticket title calls for one `resetOnAuthTransition()`. Two composable
 * functions were chosen over a single one taking an `isOrgSwitch` boolean:
 * the two resets are genuinely different scopes (per-user vs per-organisation)
 * with different callers, and a boolean-gated single function would only hide
 * that distinction behind a parameter instead of making it structural.
 * @module stores/resetPerUserState
 */
import AsyncStorage from "@react-native-async-storage/async-storage";

import { provisionalThreadsVar } from "@/features/knowledge-graph/utils/provisionalThreads";
import { EVALUATION_OUTBOX_STORAGE_KEY } from "@/features/match-report-capture/stores/evaluationOutbox";
import {
  defaultFilterValues,
  defaultFilterValuesVar,
  filterValuesVar,
} from "@/features/player-filter";
import { globalSearchFilterValuesVar } from "@/features/global-search/stores/globalSearchStore";
import { resetDismissedSuggestions } from "@/features/player-kanban/stores/dismissedSuggestionsStore";
import { resetWatchlistSelection } from "@/features/scouting/stores/watchlistSelectionStore";
import { filterValuesVar as similarPlayersFilterValuesVar } from "@/features/similar-players/stores/filterValues";
import { SimilarPlayerSortField, SortField } from "@/generated/graphql";
import { sessionGeneratedSummariesVar } from "@/stores/aiSummarySession";
import { messagesVar } from "@/stores/knowledgeGraph";
import { shadowTeamFilterValuesVar } from "@/stores/filterValues";
import { playerTabMemoryVar } from "@/stores/player";
import { recentPlayersVar } from "@/stores/recentPlayers";
import {
  similarityShowVar,
  similaritySortVar,
  watchlistShowVar,
  watchlistSortVar,
} from "@/stores/selectionSort";
import { selectedShowTeamVar } from "@/stores/shadowTeam";
import { clearDismissedActivityIds } from "@/utils/dismissedActivities";

/**
 * Clears every piece of module-scoped state that belongs to one user.
 *
 * Call this on EVERY transition that ends a user's session — deliberate
 * sign-out, organisation switch, and token expiry. A transition that skips it
 * hands the next user in the same app instance the previous one's state.
 * @returns Nothing.
 */
export const resetPerUserState = (): void => {
  // SE-8316: a live cross-list selection never outlives the session's user.
  resetWatchlistSelection();
  watchlistShowVar({ title: "GPR", type: SortField.Gpr });
  watchlistSortVar(undefined);
  similarityShowVar({
    title: "Similarity",
    type: SimilarPlayerSortField.SimilarityScore,
  });
  similaritySortVar(undefined);
  defaultFilterValuesVar(defaultFilterValues);

  /*
   * SE-7701: `defaultFilterValuesVar` holds the default RANGE DEFINITIONS, not
   * the user's selections — the two `filterValuesVar`s below hold those (player
   * search and similar players respectively). Resetting only the defaults left
   * the previous user's — or the previous organisation's — actual filter
   * selections applied to the next session in the same app instance.
   * `client.clearStore()` cannot cover this: reactive vars are module-scoped
   * and independent of the Apollo store.
   */
  filterValuesVar(null);
  similarPlayersFilterValuesVar(null);

  /*
   * SE-7721: `shadowTeamFilterValuesVar` and `globalSearchFilterValuesVar` are
   * the same shape and the same leak as the two `filterValuesVar`s above — an
   * `IFilterValues | null` holding the user's selections for one more
   * surface (Shadow Team's own filter panel, and the global search modal's
   * filter panel). SE-3827 deliberately isolated them from `filterValuesVar`
   * so selections on one surface don't bleed into another WITHIN a session;
   * that isolation says nothing about clearing them BETWEEN sessions, and
   * neither was on any of the three hand-maintained lists SE-7721 found.
   */
  shadowTeamFilterValuesVar(null);
  globalSearchFilterValuesVar(null);

  /*
   * The evaluation outbox is deliberately NOT cleared here (SE-8075).
   *
   * SE-7720 cleared it on every transition, because its entries carried no
   * identity and replayed under whoever signed in next. That was safe and it
   * destroyed a scout's unsent work — captured offline at a ground, then
   * signed out, or expired, before regaining signal.
   *
   * Each entry now records the user and organisation that queued it, and the
   * drain replays only the current session's own entries. So the queue is
   * already safe across every transition without anything running at the
   * transition, and clearing it here would only throw that work away again.
   */

  // SE-5490: pipeline suggestion dismissals are session- AND user-scoped.
  // Sign-out does not reload the web app, so without this the next user in the
  // same tab would inherit the previous user's hidden candidates.
  resetDismissedSuggestions();

  /*
   * SE-8285: the per-player tab memory. Same shape of leak as the pipeline
   * dismissals above — module-scoped UI state keyed by player, inherited by
   * whoever signs in next in the same app instance, and out of reach of
   * `client.clearStore()`.
   *
   * It is on the per-user list rather than the org-scoped one because it is a
   * disclosure, not just stale UI: a player is in this memory only because
   * someone deliberately chose a non-default tab on them, so leaving it
   * behind tells the next user on the device which players the previous one
   * was working on, and in which tab. That reading is available across an
   * organisation boundary too, since nothing about the memory is scoped to
   * the organisation the players belong to.
   */
  playerTabMemoryVar([]);

  /*
   * SE-8594: the same shape of leak as the per-player tab memory just above —
   * module-scoped state keyed by player, inherited by whoever signs in next
   * in the same app instance, and out of reach of `client.clearStore()`. It
   * is a stronger disclosure than the tab memory: the previous user's actual
   * AI-generated summary TEXT for a player, not just which tab they had open.
   */
  sessionGeneratedSummariesVar([]);

  /*
   * SE-7721: the knowledge-graph chat transcript. `useAuthOperations`'s
   * `updateAuthContext` already re-derives `currentUserVar`/`userIdVar`/
   * `organizationIdVar`/`sessionIdVar` whenever the authenticated user
   * changes, but it never cleared the messages themselves — so the previous
   * user's conversation stayed on screen until a thread was explicitly
   * selected. Clearing it here, alongside the identity re-derivation's
   * trigger points, closes that gap for all three transitions.
   */
  messagesVar([]);

  /*
   * SE-8865: finished chats the server had not listed yet, held as
   * provisional Conversations rows with their questions, answers and
   * transcripts. A deliberate sign-out also clears them through the Apollo
   * store-clear listener, but session expiry signs out without clearing the
   * store, so without this the next user would see the previous one's chats.
   */
  provisionalThreadsVar([]);
};

/**
 * Clears state that belongs to the ORGANISATION being left, not the user.
 *
 * Call this on every transition that switches which organisation the current
 * user is acting within: logging into a different organisation from the
 * Organisations list, and accepting an invitation (which also calls
 * `logIntoOrganization` under the hood, and is therefore the same kind of
 * transition — SE-7721 found it missing this exact set).
 *
 * Do NOT call this on sign-out. Sign-out already runs `resetPerUserState`,
 * and unlike that set, none of these entries need a dedicated clear when the
 * session itself ends — the whole app is torn down.
 * @returns A promise that resolves once the AsyncStorage wipe completes, so
 * callers can sequence a subsequent Apollo store reset / navigation after it.
 */
export const resetOrgScopedState = async (): Promise<void> => {
  // Shadow team selection is scoped to the organisation whose shadow teams
  // they are.
  selectedShowTeamVar(undefined);

  // Recently viewed players are meaningful only within the organisation whose
  // roster they were viewed in.
  recentPlayersVar([]);

  // Dismissed activity-feed notifications are per-organisation feed items.
  clearDismissedActivityIds();

  // Nuke all AsyncStorage except auth, theme and the evaluation outbox —
  // anything else persisted there (e.g. cached org-scoped UI preferences)
  // belongs to the organisation being left.
  //
  // The outbox is kept because it is NOT the organisation's state: it is every
  // scout's unsent work on this device, each entry already scoped to the user
  // and organisation that queued it (SE-8075). Wiping it would destroy that
  // work on web outright, where the switch reloads the page and the in-memory
  // copy that could otherwise write it back goes with it.
  try {
    const keys = await AsyncStorage.getAllKeys();
    const keysToKeep = new Set([
      "@authData",
      "@themeMode",
      EVALUATION_OUTBOX_STORAGE_KEY,
    ]);
    const keysToRemove = keys.filter(key => !keysToKeep.has(key));
    if (keysToRemove.length > 0) {
      await AsyncStorage.multiRemove(keysToRemove);
    }
  } catch (error) {
    console.warn("Failed to clear org-specific storage:", error);
    throw error;
  }
};
